import { CreateControllerFn } from '@wix/yoshi-flow-editor';

import { initServices } from '@common/services';
import { createBiLogger } from '@common/providers';
import { RootState, initStore, initActions } from './store';

import {
  createMembersAreaWidgetPluginService,
  createWidgetPluginExports,
  routerDataServiceFactory,
} from '@wix/members-area-widget-plugin-lib/viewer';
import { Status } from '@common/store/member-slice';
import { createControllerV1 } from './members-area-v1-controller';

export interface ControllerProps {
  state: RootState;
  actions: ReturnType<typeof initActions>;
  errorLogger: (error: Error) => void;
}

const createController: CreateControllerFn = async ({ flowAPI }) => {
  const isSSR = flowAPI.environment.isSSR;

  const { setProps, wixCodeApi } = flowAPI.controllerConfig;

  const widgetPluginService = createMembersAreaWidgetPluginService();

  // ! To support V1 members area
  // if (!widgetPluginService.getIsWidgetPlugin()) {
  //   return createControllerV1(flowAPI);
  // }

  // ! Everything below is for Members area 2
  const initialise = async () => {
    const fedopsLogger = flowAPI.fedops;
    const errorLogger = (error: Error) => {
      // @TODO add state update on error
      flowAPI.reportError(error);
    };
    const biLogger = createBiLogger(flowAPI.bi!, {
      defaultConfig: {
        src: 69,
        endpoint: 'forums-uou',
      },
      defaultParams: {
        origin: 'member_posts_page',
      },
    });

    const servicesConfig = initServices(flowAPI);
    const store = initStore(servicesConfig, errorLogger);
    const actions = initActions(
      wixCodeApi,
      store,
      errorLogger,
      biLogger,
      fedopsLogger,
    );

    if (!isSSR) {
      actions.logBiEvent({ evid: 118, type: 'member_posts_page' });
    }

    // Here you can pass props to the Widget
    const routerDataService = routerDataServiceFactory(
      widgetPluginService,
      wixCodeApi.user,
      wixCodeApi.window,
    );

    actions.initAppBase({
      // @TODO add language validation from available forum languages
      language: wixCodeApi.site.language || 'en',
    });

    if (!widgetPluginService.getIsWidgetPlugin()) {
      const viewedUserId = (await routerDataService.getRouteData())
        ?.viewedMemberIdentifier?.id;

      return actions.initAppData({ viewedUserId: viewedUserId || '' });
    }

    widgetPluginService.onMembersAreaEvent(async (event) => {
      switch (event.type) {
        case 'MEMBERS_CHANGED':
          return actions.resetMemberPosts();
        case 'TAB_CHANGED':
          const membersPosts = store.getState().memberPosts;
          const newViewedUserId = (await routerDataService.getRouteData())
            ?.viewedMemberIdentifier?.id;
          if (
            membersPosts.status !== Status.INITIAL ||
            !newViewedUserId ||
            event.payload !== '14f0266c-e0a3-afc6-d07c-5be8e20949cd'
          ) {
            return;
          }
          return actions.initAppData({ viewedUserId: newViewedUserId });
      }
    });

    setProps({
      actions,
      state: store.getState(),
      fitToContentHeight: true,
    });

    store.subscribe(() => {
      setProps({
        actions,
        state: store.getState(),
      });
    });
  };

  return {
    async pageReady() {
      if (!widgetPluginService.getIsWidgetPlugin()) {
        return createControllerV1(flowAPI);
      }
      const isRendered = widgetPluginService.getIsRendered();

      if (!isRendered) {
        setProps({ isRendered });
        return Promise.resolve();
      }

      await initialise();
    },
    exports() {
      return createWidgetPluginExports(widgetPluginService, initialise);
    },
  };
};

export default createController;
